/** tempexp_14729-full-file */
import classNames from 'clsx';
import React, { type FC, memo, useCallback, useRef } from 'react';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import type { Kind } from 'reactApp/types/Tree';
import { sendDatalistThumbDwh } from 'reactApp/ui/Datalist/DataList.helpers';
import type { IThumb } from 'reactApp/ui/DataListItem/DataListItem.helpers';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { getIconType } from 'reactApp/ui/FileIcon/getIconType';
import { ImageCancelable } from 'reactApp/ui/ImageCancelable/ImageCancelable';

import styles from './Pic.css';

export interface PicFolderProps {
    kind: Kind;
    isAutoDelete?: boolean;
    isLoading?: boolean;
    isPublic: boolean;
    id?: string;
}

export interface PicFileProps {
    thumbUrl?: IThumb;
    ext: string;
    isVirus: boolean;
}

type PicProps = {
    className?: string;
    id?: string | undefined;
    author?: { name?: string; email: string };
    isFolder: boolean;
    onLoad?: () => void;
    hasLoaded?: boolean;
} & PicFolderProps &
    PicFileProps;

export const Pic: FC<PicProps> = memo(
    ({ className, isFolder, kind, ext, isVirus, isAutoDelete, isLoading, thumbUrl, onLoad, hasLoaded = false, id }) => {
        const iconType = getIconType({
            isFolder,
            kind: isFolder ? kind : undefined,
            ext: isFolder ? undefined : ext.toLowerCase(),
            isVirus: isFolder ? false : isVirus,
            isAutoDelete: isFolder ? isAutoDelete : false,
            id,
        });

        const isLoadingFolder = isFolder ? isLoading : false;

        const { src, srcSet } = thumbUrl || {};

        const startTime = useRef(performance.now());

        const handleThumbLoad = useCallback(() => {
            const endTime = performance.now();

            sendDatalistThumbDwh('load', EViewMode.thumbs, kind, Math.round(endTime - startTime.current));

            onLoad?.();
        }, [onLoad, kind]);

        const handleThumbError = useCallback(() => {
            const endTime = performance.now();

            sendDatalistThumbDwh('fail', EViewMode.thumbs, kind, Math.round(endTime - startTime.current), src);
        }, [kind, src]);

        return (
            <div className={classNames(styles.root, className)}>
                {!hasLoaded && (
                    <div
                        className={classNames(styles.icon, {
                            [styles.loading_folder]: isLoadingFolder,
                        })}
                    >
                        <FileIcon type={iconType} size="l" />
                    </div>
                )}
                {!isFolder && Boolean(thumbUrl?.src) && (
                    <div className={styles.thumb_container}>
                        <ImageCancelable
                            className={classNames(styles.thumb, {
                                [styles.thumb_loaded]: hasLoaded,
                            })}
                            draggable={false}
                            src={src}
                            srcSet={srcSet ? `${srcSet} 2x` : ''}
                            alt=""
                            onError={handleThumbError}
                            onLoad={handleThumbLoad}
                            data-loaded={hasLoaded}
                        />
                    </div>
                )}
            </div>
        );
    }
);

Pic.displayName = 'Pic';
